import type { CoachingClip, CoachingNote, Player } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { EvidenceList, feedbackMeta } from "./EvidenceList";

export type NotesAndClipsRailProps = {
  notes: CoachingNote[];
  clips: CoachingClip[];
  players?: Player[];
  activeNoteId: number | null;
  activeClipId?: number | null;
  onJumpToNote: (note: CoachingNote) => void;
  onOpenClip: (clip: CoachingClip) => void;
  notesLoading?: boolean;
  clipsLoading?: boolean;
  covered?: boolean;
};
export function NotesAndClipsRail({
  notes,
  clips,
  players,
  activeNoteId,
  activeClipId,
  onJumpToNote,
  onOpenClip,
  notesLoading,
  clipsLoading,
  covered = false,
}: NotesAndClipsRailProps) {
  return (
    <div
      className={`review-evidence-panel ${covered ? "pointer-events-none opacity-40" : ""}`}
      aria-hidden={covered || undefined}
      data-covered={covered ? "true" : undefined}
      data-testid="coach-review-notes-clips-rail"
    >
      {covered ? (
        <p data-testid="coach-review-rail-covered-hint">
          Close the editor to browse these, or switch notes from the editor
          header.
        </p>
      ) : null}
      <EvidenceList
        label="Notes on this match"
        players={players}
        loading={notesLoading}
        empty="No notes yet — mark a moment to add one."
        testId="coach-review-notes-strip"
        rowTestId="coach-review-rail-note"
        onOpen={onJumpToNote}
        rows={notes.map((n) => ({
          value: n,
          id: n.id,
          title: n.title || "Note",
          position: formatPlayhead(n.timestamp_seconds),
          metadata: feedbackMeta("Note", n.slot, n.category, n.visibility),
          visibility: n.visibility,
          playerIds: n.player_ids,
          active: activeNoteId === n.id,
        }))}
      />
      <EvidenceList
        label="Clips on this match"
        players={players}
        loading={clipsLoading}
        empty="No clips yet — make one from the clip track."
        testId="coach-review-clips-strip"
        rowTestId="coach-review-rail-clip"
        onOpen={onOpenClip}
        rows={clips.map((c) => ({
          value: c,
          id: c.id,
          title: c.title || "Clip",
          position: `${formatPlayhead(c.start_seconds)}–${formatPlayhead(c.end_seconds)}`,
          metadata: feedbackMeta("Clip", c.slot, c.category, c.visibility),
          duration: `${Math.max(0, c.end_seconds - c.start_seconds)
            .toFixed(1)
            .replace(/\.0$/, "")}s`,
          visibility: c.visibility,
          playerIds: c.player_ids,
          active: activeClipId === c.id,
        }))}
      />
    </div>
  );
}
